import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { materialService, transactionService } from "/app/src/services";
import { Material, Transaction as TransactionType } from "/app/src/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { useCallback, useEffect, useState } from "react";
import { useForm, useController } from "react-hook-form";
import { ExportStateTypes } from "/app/src/constants/types";
import Box from "/app/src/components/generic/components/box";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";
import { Button, Form, Select, SelectItem } from "@heroui/react";

interface TransactionFormValues {
  exportStateType: number | undefined;
}

const TRANSACTION_DYNAMIC_IGNORE_KEYS = new Set<string>([
  "id",
  "orderId",
  "orderName",
  "warehouseName",
  "materialName",
  "orderLineId",
  "binName",
  "locationName",
  "batchName",
  "carrierName",
  "shelfName",
  "storageUnitName",
  "stationName",
  "costCenterName",
  "userName",
  "exportStateType",
  "number",
  "type",
  "motiveType",
  "handlingUnit",
  "serialNumber",
  "qualification",
  "lot",
  "quantityRequested",
  "quantityConfirmed",
  "quantityDeviated",
  "xStep",
  "yStep",
  "zoneName",
  "directionType",
  "taskNumber",
  "reasonCode",
  "creationDate",
  "expiryDate",
  "productionDate",
  "uploadDate",
  "releaseDate",
  "materialInfo1",
  "orderLineInfo1",
  "orderLineInfo2",
  "accessPointId",
  "rank",
  "unitOfMeasure",
  "quantityTotal",
  "typeDescription",
  "motiveTypeDescription",
]);

/**
 *
 * @returns Single Transaction
 */
export default function Transaction() {
  const { t } = useTranslation();
  const { transactionId } = useParams();
  const handleNavigationError = useHandleNavigationError();
  const queryClient = useQueryClient();
  const [materialId, setMaterialId] = useState<string | undefined>(undefined);
  const [dynamicFields, setDynamicFields] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const { data: transaction } = useQuery({
    queryKey: ["transaction", transactionId],
    queryFn: () => {
      return transactionService.getSingle(transactionId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { transaction: {} },
    retry: false,
    select: (data: { transaction: TransactionType }) => {
      return data.transaction;
    },
    onSuccess: (data: TransactionType) => {
      // extract dynamic fields
      if (!data) return;

      const newDynamicFields = Object.entries(data)
        .filter(
          ([key, value]) =>
            !TRANSACTION_DYNAMIC_IGNORE_KEYS.has(key) && value !== null,
        )
        .map(([key, value]) => ({ label: key, value: String(value) }));

      setDynamicFields(newDynamicFields);
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<TransactionFormValues>({
    mode: "onChange",
    defaultValues: { exportStateType: undefined },
  });

  useEffect(() => {
    // when transaction loads, set the initial form values
    reset({ exportStateType: transaction?.exportStateType });
  }, [transaction, reset]);

  const { field: exportStateField } = useController<TransactionFormValues>({
    name: "exportStateType",
    control,
    defaultValue: undefined,
  });

  useQuery({
    // depend on the transaction materialName so the query can be enabled when available
    queryKey: ["materialByName", transaction?.materialName],
    // only run when we have a materialName to match against
    enabled: Boolean(transaction?.materialName),
    // return the raw service response here
    queryFn: () => materialService.getAll(),
    // select the single material that matches the transaction materialName
    initialData: undefined,
    select: (data: { materials: Material[] }): Material | undefined => {
      return data.materials.find((m) => m.name === transaction?.materialName);
    },
    onSuccess: (selected?: Material) => {
      if (selected?.id) setMaterialId(selected.id);
    },
  });

  const selectedExportStateKeys = exportStateField.value
    ? [String(exportStateField.value)]
    : [];

  const handleExportStateChange = useCallback(
    (keys: Set<string>) => {
      const first = Array.from(keys)[0];
      exportStateField.onChange(first ? Number(first) : undefined);
    },
    [exportStateField],
  );

  //useQuery that updates the transaction
  const { mutateAsync: updateTransaction } = useMutation({
    mutationFn: (updatedTransaction: TransactionType) => {
      return transactionService.updateSingle(
        updatedTransaction.id,
        updatedTransaction,
      );
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["transaction", transactionId], response);
    },
  });

  const getTypeText = (type: number) => {
    switch (type) {
      case 0:
        return t("translation:not_set");
      case 1:
        return t("translation:manual_put");
      case 2:
        return t("translation:manual_pick");
      case 3:
        return t("translation:order_put");
      case 4:
        return t("translation:order_pick");
      case 5:
        return t("translation:transfer");
      case 6:
        return t("translation:order_count");
      case 7:
        return t("translation:context_count");
      case 8:
        return t("translation:material_rename");
      case 9:
        return t("translation:manual_correction");
      case 10:
        return t("translation:context_correction");
      case 11:
        return t("translation:cancel_request");
      case 12:
        return t("translation:purge");
      case 13:
        return t("translation:production");
      case 15:
        return t("translation:kit_rename");
      default:
        return t("translation:not_set");
    }
  };

  const basicInfo = [
    {
      label: t("translation:material_name"),
      value: transaction?.materialName,
      link: `/explorer/materials/${materialId}`,
    },
    {
      label: t("translation:creation_date"),
      value: (
        <PowerPickTimeZoneToLocal
          date={transaction?.creationDate}
          format={"MMMM Do YYYY, h:mm:ss.SS a"}
        />
      ),
    },
    {
      label: t("translation:type"),
      value: getTypeText(Number(transaction?.type)),
    },
    {
      label: t("translation:export_state_type"),
      value: ExportStateTypes.find(
        (type) => type.value === transaction?.exportStateType,
      )?.name,
    },
    {
      label: t("translation:motive_type"),
      value: transaction?.motiveType,
    },
    {
      label: t("translation:requested_quantity"),
      value: transaction?.quantityRequested,
    },
    {
      label: t("translation:confirmed_quantity"),
      value: transaction?.quantityConfirmed,
    },
    {
      label: t("translation:deviated_quantity"),
      value: transaction?.quantityDeviated,
    },
  ];

  const orderInfo = [
    {
      label: t("translation:order_name"),
      value: transaction?.orderName,
      link: `/explorer/orders/${transaction?.orderId}`,
    },
    { label: t("translation:line"), value: transaction?.number },
  ];

  const additionalInfo = [
    { label: t("translation:lot"), value: transaction?.lot },
    { label: t("translation:user"), value: transaction?.userName },
    { label: t("translation:warehouse"), value: transaction?.warehouseName },
    { label: t("translation:location"), value: transaction?.locationName },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{transaction?.id}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: "Transactions", to: "/explorer/transactions" },
          { label: transaction?.id },
        ]}
      />
      <h1 className="text-[36px] font-medium">{transaction?.materialName}</h1>
      <h2 className="text-[16px] mb-6 text-neutral-smoke">
        Transaction Id: {transaction?.id}
      </h2>
      <InfoList header={t("translation:basic_info")} items={basicInfo} />
      <InfoList header={t("translation:order_info")} items={orderInfo} />
      <InfoList
        header={t("translation:additional_info")}
        items={additionalInfo}
      />
      {dynamicFields.length > 0 && (
        <InfoList
          header={t("translation:dynamic_fields")}
          items={dynamicFields}
        />
      )}
      <Box>
        <Form
          onSubmit={handleSubmit(async (data) => {
            if (transaction?.id) {
              await updateTransaction({
                id: transaction.id,
                exportStateType: data.exportStateType,
              });
            }
          })}
          className="flex flex-col gap-4 w-full"
        >
          <div className="flex items-center justify-between w-full">
            <h3 className="text-[16px] font-medium mb-4">
              {t("translation:export_state_type")}
            </h3>
            <Button
              type="submit"
              isDisabled={!isDirty || isSubmitting}
              color="primary"
            >
              {t("translation:save")}
            </Button>
          </div>
          <Select
            aria-label="Export state"
            className="w-[50%]"
            // map controller value -> selectedKeys (string)
            selectedKeys={selectedExportStateKeys}
            onSelectionChange={handleExportStateChange}
          >
            {ExportStateTypes.map((option) => (
              <SelectItem key={String(option.value)}>{option.name}</SelectItem>
            ))}
          </Select>
        </Form>
      </Box>
    </div>
  );
}
